/**
 * Freight tab: table with row actions, editable cells, and matched NEX line (same pattern as Line Items tab).
 */
import React, { useMemo } from 'react';
import {
    Box,
    Button,
    HStack,
    Icon,
    Table,
    Tbody,
    Td,
    Thead,
    Tr,
} from '@chakra-ui/react';
import { TbPlus } from 'react-icons/tb';
import type { InvoiceLineItemsSectionProps } from './InvoiceLineItemsSection';
import type { ReconciliationMatchNote } from '../../types/invoice';
import { getSignedNexLineDisplayQty, getSignedNexLineDisplayTotal } from '../../utils/nexLineQuantity';
import { formatNexMatchedLinePoLabel } from '../../utils/nexMatchedLinePoLabel';
import {
    INVOICE_TAB_ACCENT,
    INVOICE_TAB_ACCENT_HOVER,
    InvoiceIssueFlagButton,
    InvoicePartGridTableContainer,
    InvoiceMatchNoteInfoButton,
    InvoiceMatchedNexRows,
    InvoiceMatchedNexExpandAllHeaderControl,
    InvoiceMatchedNexToggleButton,
    InvoiceSuggestionBulbButton,
    InvoiceStandardHeaderRow,
    buildListPriceHeaderContent,
    getInvoiceItemsTableSx,
    InvoicePartTableRowActionsCell,
    VENDOR_MISMATCH_SUGGESTION_DISABLED_REASON,
    invoicePartGridLandingInputPropsWithCaretAtEnd,
} from './InvoiceTabShared';
import { parseSignedQtyCommit } from '../../utils/invoiceNumericInput';
import { LineItemGridLandingEditable } from './LineItemGridLandingEditable';
import { useInvoicePartGridInteractions } from './useInvoicePartGridInteractions';
import { useInvoicePartTableRowDrag } from './useInvoicePartTableRowDrag';

function freightRowMissingInNex(flaggedParts: any[], index: number) {
    return !!flaggedParts.find(
        (issue: any) =>
            issue.issueType === 'missing_in_nex' &&
            issue.issueDetails?.scope === 'freight' &&
            issue.partIndex === -(index + 1)
    );
}

export function InvoiceFreightTab(props: InvoiceLineItemsSectionProps) {
    const {
        invoice,
        editedFields,
        addFreightItem,
        addFreightItemAt,
        addFreightItemAbove,
        duplicateFreightItemAt,
        removeFreightItem,
        reorderFreightItems,
        updateFreightItem,
        getFreightItemStringValue,
        renderFreightItemNumericCell,
        fmtUsd,
        isReadOnly,
        expandedMatchedNexRows,
        setExpandedMatchedNexRows,
        setUnmatchConfirmModal,
        unmatchedPartIndices,
        localMatchedNexOverrides,
        openReconciliationDetailModal,
        getIssuesForPart,
        getPartNumberChoiceIssue,
        getMissingInNexSuggestion,
        getIssueTooltip,
        flaggedPartIndices,
        setEditingNumericField,
        setEditingNumericValues,
        onLineItemImageHighlightChange,
        showTableGridlines = true,
        effectiveMatchedNexLines,
        effectiveReconciliationMatchNotes,
        nexLineQuantityBasis = 'qtyRec',
    } = props as InvoiceLineItemsSectionProps & { isReadOnly?: boolean };

    const lineItems = editedFields.lineItems ?? invoice?.lineItems ?? [];
    const freightItems = useMemo(
        () => editedFields.freightItems ?? invoice?.freightItems ?? [],
        [editedFields.freightItems, invoice?.freightItems]
    );
    const hasItems = freightItems.length > 0;

    const spreadsheet = useInvoicePartGridInteractions({
        rowCount: freightItems.length,
        rows: freightItems,
        getStringValue: getFreightItemStringValue,
        updateCell: updateFreightItem,
        isReadOnly: !!isReadOnly,
        setEditingNumericField: setEditingNumericField ?? (() => {}),
        setEditingNumericValues,
        numericKeyPrefix: 'freightItem',
    });

    const rowDrag = useInvoicePartTableRowDrag({ isReadOnly: !!isReadOnly, onReorder: reorderFreightItems });

    const gridEditableInputProps = invoicePartGridLandingInputPropsWithCaretAtEnd();
    const flaggedParts = invoice?.reconciliationIssues?.flaggedParts ?? [];
    const suppressFlagAndSuggestionUi = !!invoice?.reconciliationIssues?.vendorNameMismatch;
    const vendorMismatchDisablesSuggestions = !!invoice?.reconciliationIssues?.vendorNameMismatch;
    const roNotFound = !!invoice?.reconciliationIssues?.roNumberNotFound;

    const matchedNexLines = useMemo(
        () => effectiveMatchedNexLines ?? invoice?.reconciliationIssues?.matchedNexLines ?? [],
        [effectiveMatchedNexLines, invoice]
    );
    const baseMatchNotes: ReconciliationMatchNote[] =
        effectiveReconciliationMatchNotes ??
        (invoice?.reconciliationIssues as { reconciliationMatchNotes?: ReconciliationMatchNote[] } | undefined)
            ?.reconciliationMatchNotes ??
        [];

    const getFreightExpandKey = (index: number) => `freight-nex-${lineItems.length}-${index}`;

    const freightMatchedExpandKeys = useMemo(() => {
        const keys: string[] = [];
        freightItems.forEach((item, index) => {
            const partIndexGlobal = lineItems.length + index;
            let matchedNex = matchedNexLines.find((m: { partIndex: number; nexLine: any }) => m.partIndex === partIndexGlobal)?.nexLine;
            if (unmatchedPartIndices.has(partIndexGlobal)) matchedNex = undefined;
            const ov = localMatchedNexOverrides[partIndexGlobal];
            if (ov !== undefined) matchedNex = ov;
            if (matchedNex) keys.push(`freight-nex-${lineItems.length}-${index}`);
        });
        return keys;
    }, [freightItems, lineItems.length, matchedNexLines, unmatchedPartIndices, localMatchedNexOverrides]);

    const allFreightMatchedNexExpanded =
        freightMatchedExpandKeys.length > 0 && freightMatchedExpandKeys.every((k) => expandedMatchedNexRows.has(k));

    if (!hasItems) {
        return (
            <>
                <Box textAlign="center" py={6}>
                    <Button
                        size="sm"
                        bg={INVOICE_TAB_ACCENT}
                        color="white"
                        leftIcon={<Icon as={TbPlus as React.ElementType} boxSize={4} />}
                        onClick={addFreightItem}
                        _hover={{ bg: INVOICE_TAB_ACCENT_HOVER }}
                        isDisabled={!!isReadOnly}
                    >
                        Add Freight Item
                    </Button>
                </Box>
            </>
        );
    }

    return (
        <>
            <InvoicePartGridTableContainer
                containerRef={spreadsheet.containerRef}
                isReadOnly={!!isReadOnly}
                onMouseDownCapture={spreadsheet.onContainerMouseDownCapture}
                onPaste={spreadsheet.onPaste}
            >
                <Table variant="simple" size="sm" sx={getInvoiceItemsTableSx(showTableGridlines)}>
                    <Thead>
                        <InvoiceStandardHeaderRow
                            firstColumnContent={
                                <InvoiceMatchedNexExpandAllHeaderControl
                                    isVisible={freightMatchedExpandKeys.length > 0}
                                    allExpanded={allFreightMatchedNexExpanded}
                                    onToggle={() => {
                                        setExpandedMatchedNexRows((prev) => {
                                            const next = new Set(prev);
                                            if (allFreightMatchedNexExpanded) freightMatchedExpandKeys.forEach((k) => next.delete(k));
                                            else freightMatchedExpandKeys.forEach((k) => next.add(k));
                                            return next;
                                        });
                                    }}
                                />
                            }
                            listPriceHeaderContent={
                                buildListPriceHeaderContent({
                                    invoiceType: invoice?.invoiceType,
                                    subletListPriceMarkup: invoice?.subletListPriceMarkup,
                                    subletListPriceDiscount: invoice?.subletListPriceDiscount,
                                    lkqListPriceMarkup: invoice?.lkqListPriceMarkup,
                                    lkqListPriceDiscount: invoice?.lkqListPriceDiscount,
                                }) ?? 'List Price'
                            }
                        />
                    </Thead>
                    <Tbody>
                        {freightItems.map((item, index) => {
                            const partIndexGlobal = lineItems.length + index;
                            const freightIssuePartIndex = -(index + 1);
                            let matchedNex = matchedNexLines.find((m: { partIndex: number; nexLine: any }) => m.partIndex === partIndexGlobal)?.nexLine as
                                | {
                                      partDesc?: string;
                                      partNumber?: string;
                                      qtyOnOrder?: number;
                                      qty?: number;
                                      listPrice?: number;
                                      unitCost?: number;
                                      extendedCost?: number;
                                      poHeaderID?: number;
                                      lineNumber?: number;
                                      unqSeq?: number | null;
                                      _poVendorName?: string;
                                  }
                                | undefined;
                            if (unmatchedPartIndices.has(partIndexGlobal)) matchedNex = undefined;
                            const ov = localMatchedNexOverrides[partIndexGlobal];
                            if (ov !== undefined) matchedNex = ov;

                            const hasMatchedNex = !!matchedNex;
                            const expandKey = getFreightExpandKey(index);
                            const isNexExpanded = expandedMatchedNexRows.has(expandKey);
                            const nexQty = getSignedNexLineDisplayQty(
                                matchedNex,
                                invoice?.invoiceType,
                                nexLineQuantityBasis,
                            );
                            const nexTotalRaw = matchedNex?.extendedCost ?? Math.abs(nexQty) * (matchedNex?.unitCost ?? 0);
                            const nexTotal = getSignedNexLineDisplayTotal(nexTotalRaw, invoice?.invoiceType);
                            const nexMatchedVendorName = matchedNex?._poVendorName?.trim() ?? '';
                            const { label: nexMatchedPoLineLabel, status: nexMatchedStatus } = formatNexMatchedLinePoLabel(
                                matchedNex,
                                invoice?.nexResponse ?? null,
                            );
                            const matchNote = baseMatchNotes.find((n) => n.partIndex === partIndexGlobal);
                            const hasMatchNote = !!matchNote;

                            const rowFlagged = !suppressFlagAndSuggestionUi && freightRowMissingInNex(flaggedParts, index);
                            const isUserUnmatched = unmatchedPartIndices.has(partIndexGlobal);
                            const isManuallyAdded = (item.confidence ?? 0) === 0;
                            const isFlagged =
                                !suppressFlagAndSuggestionUi &&
                                (flaggedPartIndices.has(partIndexGlobal) ||
                                    flaggedPartIndices.has(freightIssuePartIndex) ||
                                    isUserUnmatched);
                            const issues = getIssuesForPart(freightIssuePartIndex);
                            const showRowHighlight = isManuallyAdded || (!hasMatchedNex && (isFlagged || rowFlagged));

                            return (
                                <React.Fragment key={index}>
                                    <Tr
                                        bg={showRowHighlight ? 'orange.50' : 'white'}
                                        borderLeft={showRowHighlight ? '3px solid' : undefined}
                                        borderLeftColor={showRowHighlight ? 'orange.400' : undefined}
                                        _hover={{ bg: showRowHighlight ? 'orange.100' : 'gray.50' }}
                                        role="group"
                                        {...rowDrag.getDraggableRowProps(index)}
                                        onMouseEnter={() => {
                                            if (item.imageSourceHighlight) {
                                                onLineItemImageHighlightChange?.(item.imageSourceHighlight);
                                            }
                                        }}
                                        onMouseLeave={() => {
                                            onLineItemImageHighlightChange?.(null);
                                        }}
                                    >
                                        <Td py={3} px={2} borderColor="gray.100" position="relative" w="116px" minW="116px">
                                            <InvoicePartTableRowActionsCell
                                                isReadOnly={!!isReadOnly}
                                                onAddAbove={() => addFreightItemAbove(index)}
                                                onAddBelow={() => addFreightItemAt(index)}
                                                onDuplicate={() => duplicateFreightItemAt(index)}
                                                onDelete={() => removeFreightItem(index)}
                                            />
                                            <HStack spacing={0.5} align="center" justify="flex-start" pl={5} flexWrap="nowrap">
                                                    {hasMatchedNex && (
                                                        <InvoiceMatchedNexToggleButton
                                                            isExpanded={isNexExpanded}
                                                            onClick={() =>
                                                                setExpandedMatchedNexRows((prev) => {
                                                                    const next = new Set(prev);
                                                                    if (next.has(expandKey)) next.delete(expandKey);
                                                                    else next.add(expandKey);
                                                                    return next;
                                                                })
                                                            }
                                                        />
                                                    )}
                                                    {!suppressFlagAndSuggestionUi && !hasMatchedNex && isUserUnmatched && (
                                                        <InvoiceIssueFlagButton
                                                            tooltip="Line item will be added to NEX without a matching NEX line"
                                                            ariaLabel="Orphaned line item"
                                                        />
                                                    )}
                                                    {!suppressFlagAndSuggestionUi && !hasMatchedNex && !isUserUnmatched && isFlagged && issues.length > 0 && (
                                                        <InvoiceIssueFlagButton tooltip={getIssueTooltip(issues)} />
                                                    )}
                                                    {!suppressFlagAndSuggestionUi && !hasMatchedNex && !roNotFound && !isReadOnly && (
                                                        <InvoiceSuggestionBulbButton
                                                            tooltip={
                                                                getPartNumberChoiceIssue(freightIssuePartIndex)
                                                                    ? 'Part number differs from NEX'
                                                                    : getMissingInNexSuggestion(freightIssuePartIndex)
                                                                      ? 'Part not found – possible match'
                                                                      : 'Click to view suggested matching freight line items from NEX'
                                                            }
                                                            onClick={() => {
                                                                const iss = getIssuesForPart(freightIssuePartIndex);
                                                                openReconciliationDetailModal({
                                                                    type: 'flag',
                                                                    partIndex: freightIssuePartIndex,
                                                                    issue: iss[0],
                                                                });
                                                            }}
                                                        />
                                                    )}
                                                    {vendorMismatchDisablesSuggestions && !hasMatchedNex && !roNotFound && !isReadOnly && (
                                                        <InvoiceSuggestionBulbButton
                                                            tooltip={VENDOR_MISMATCH_SUGGESTION_DISABLED_REASON}
                                                            disabled
                                                            ariaLabel="Suggestion disabled due to vendor mismatch"
                                                        />
                                                    )}
                                                    {hasMatchNote && matchNote && (
                                                        <InvoiceMatchNoteInfoButton
                                                            tooltip={matchNote.reason}
                                                            onClick={() =>
                                                                openReconciliationDetailModal({
                                                                    type: 'match_note',
                                                                    matchNote: {
                                                                        invoicePartNumber: matchNote.invoicePartNumber,
                                                                        nexPartNumber: matchNote.nexPartNumber,
                                                                        reason: matchNote.reason,
                                                                        partIndex: partIndexGlobal,
                                                                    },
                                                                })
                                                            }
                                                        />
                                                    )}
                                            </HStack>
                                        </Td>
                                        <Td
                                            py={3}
                                            fontSize="sm"
                                            textAlign="left"
                                            borderColor="gray.100"
                                            data-line-paste-cell
                                            data-row={index}
                                            data-col={0}
                                            sx={spreadsheet.getCellPasteSelectionSx(index, 0)}
                                        >
                                            <LineItemGridLandingEditable
                                                value={getFreightItemStringValue(index, 'title', '')}
                                                onChange={(v) => updateFreightItem(index, 'title', v)}
                                                placeholder="Description"
                                                isReadOnly={!!isReadOnly}
                                                editableRootProps={{ w: '100%' }}
                                                editablePreviewProps={{
                                                    display: 'block',
                                                    w: '100%',
                                                    textAlign: 'left',
                                                    color: 'gray.900',
                                                    py: 0,
                                                    px: 0,
                                                }}
                                                editableInputProps={{ ...gridEditableInputProps, textAlign: 'left' }}
                                            />
                                        </Td>
                                        <Td
                                            py={3}
                                            fontSize="sm"
                                            borderColor="gray.100"
                                            whiteSpace="nowrap"
                                            minW="120px"
                                            data-line-paste-cell
                                            data-row={index}
                                            data-col={1}
                                            sx={spreadsheet.getCellPasteSelectionSx(index, 1)}
                                        >
                                            <LineItemGridLandingEditable
                                                value={getFreightItemStringValue(index, 'partNumber', '')}
                                                onChange={(v) => updateFreightItem(index, 'partNumber', v)}
                                                placeholder="Part #"
                                                isReadOnly={!!isReadOnly}
                                                editableRootProps={{ flex: '1', minW: 0 }}
                                                editablePreviewProps={{
                                                    w: '100%',
                                                    cursor: 'pointer',
                                                    color: 'gray.900',
                                                    py: 0,
                                                    px: 0,
                                                    whiteSpace: 'nowrap',
                                                }}
                                                editableInputProps={{ ...gridEditableInputProps, textAlign: 'left' }}
                                            />
                                        </Td>
                                        <Td
                                            py={3}
                                            fontSize="sm"
                                            textAlign="right"
                                            borderColor="gray.100"
                                            data-line-paste-cell
                                            data-row={index}
                                            data-col={2}
                                            sx={spreadsheet.getCellPasteSelectionSx(index, 2)}
                                        >
                                            <LineItemGridLandingEditable
                                                value={getFreightItemStringValue(index, 'qty', '0')}
                                                onChange={(v) => updateFreightItem(index, 'qty', parseSignedQtyCommit(v))}
                                                isReadOnly={!!isReadOnly}
                                                editableRootProps={{ flex: '1', textAlign: 'right' }}
                                                editablePreviewProps={{
                                                    w: '100%',
                                                    cursor: 'pointer',
                                                    textAlign: 'right',
                                                    color: 'gray.900',
                                                    py: 0,
                                                    px: 0,
                                                }}
                                                editableInputProps={{
                                                    type: 'text',
                                                    inputMode: 'text',
                                                    ...gridEditableInputProps,
                                                    textAlign: 'right',
                                                }}
                                            />
                                        </Td>
                                        <Td
                                            py={3}
                                            fontSize="sm"
                                            textAlign="right"
                                            borderColor="gray.100"
                                            whiteSpace="nowrap"
                                            minW="90px"
                                            data-line-paste-cell
                                            data-row={index}
                                            data-col={3}
                                            sx={spreadsheet.getCellPasteSelectionSx(index, 3)}
                                        >
                                            <HStack spacing={1} align="center" justify="flex-end">
                                                <Box flex="1" minW={0} textAlign="right">
                                                    {renderFreightItemNumericCell(index, 'listPrice')}
                                                </Box>
                                                {hasMatchNote &&
                                                    matchNote?.extractedFromPage &&
                                                    Math.abs(
                                                        (matchNote.extractedFromPage.listPrice ?? 0) -
                                                            (item.listPrice ?? 0)
                                                    ) > 0.005 && (
                                                        <InvoiceMatchNoteInfoButton
                                                            tooltip={matchNote.reason}
                                                            onClick={() =>
                                                                openReconciliationDetailModal({
                                                                    type: 'match_note',
                                                                    matchNote: {
                                                                        invoicePartNumber: matchNote.invoicePartNumber,
                                                                        nexPartNumber: matchNote.nexPartNumber,
                                                                        reason: matchNote.reason,
                                                                        partIndex: partIndexGlobal,
                                                                        extractedFromPage: matchNote.extractedFromPage,
                                                                    },
                                                                })
                                                            }
                                                        />
                                                    )}
                                            </HStack>
                                        </Td>
                                        <Td
                                            py={3}
                                            fontSize="sm"
                                            textAlign="right"
                                            borderColor="gray.100"
                                            whiteSpace="nowrap"
                                            minW="90px"
                                            data-line-paste-cell
                                            data-row={index}
                                            data-col={4}
                                            sx={spreadsheet.getCellPasteSelectionSx(index, 4)}
                                        >
                                            <HStack spacing={1} align="center" justify="flex-end">
                                                <Box flex="1" minW={0} textAlign="right">
                                                    {renderFreightItemNumericCell(index, 'unitBuyPrice')}
                                                </Box>
                                                {hasMatchNote &&
                                                    matchNote?.extractedFromPage &&
                                                    Math.abs(
                                                        (matchNote.extractedFromPage.unitBuyPrice ?? 0) -
                                                            (item.unitBuyPrice ?? 0)
                                                    ) > 0.005 && (
                                                        <InvoiceMatchNoteInfoButton
                                                            tooltip={matchNote.reason}
                                                            onClick={() =>
                                                                openReconciliationDetailModal({
                                                                    type: 'match_note',
                                                                    matchNote: {
                                                                        invoicePartNumber: matchNote.invoicePartNumber,
                                                                        nexPartNumber: matchNote.nexPartNumber,
                                                                        reason: matchNote.reason,
                                                                        partIndex: partIndexGlobal,
                                                                        extractedFromPage: matchNote.extractedFromPage,
                                                                    },
                                                                })
                                                            }
                                                        />
                                                    )}
                                            </HStack>
                                        </Td>
                                        <Td
                                            py={3}
                                            fontSize="sm"
                                            textAlign="right"
                                            fontWeight="medium"
                                            borderColor="gray.100"
                                            whiteSpace="nowrap"
                                            minW="90px"
                                            data-line-paste-cell
                                            data-row={index}
                                            data-col={5}
                                            sx={spreadsheet.getCellPasteSelectionSx(index, 5)}
                                        >
                                            {renderFreightItemNumericCell(index, 'totalBuyPrice', 'medium')}
                                        </Td>
                                    </Tr>
                                    {hasMatchedNex ? (
                                        <InvoiceMatchedNexRows
                                            isExpanded={isNexExpanded}
                                                matchedNex={matchedNex!}
                                            nexQty={nexQty}
                                            nexTotal={nexTotal}
                                            nexMatchedVendorName={nexMatchedVendorName}
                                                nexMatchedPoLineLabel={nexMatchedPoLineLabel ?? undefined}
                                                nexMatchedStatus={nexMatchedStatus ?? undefined}
                                            showTableGridlines={showTableGridlines}
                                            isReadOnly={!!isReadOnly}
                                            onUnmatch={() =>
                                                setUnmatchConfirmModal({
                                                    partIndex: partIndexGlobal,
                                                    partNumber: item?.partNumber,
                                                    nexPartNumber: matchedNex?.partNumber,
                                                    nexLine: matchedNex,
                                                })
                                            }
                                            formatMoney={fmtUsd}
                                        />
                                    ) : null}
                                </React.Fragment>
                            );
                        })}
                    </Tbody>
                </Table>
            </InvoicePartGridTableContainer>
        </>
    );
}
